import L from 'leaflet'
import { useTheme } from '@mui/material'
import { useEffect, useRef, useMemo } from 'react'
import { useMap, useMapEvents } from 'react-leaflet'

const EDGE_SNAP_PX = 12 // pixels — threshold for showing ghost vertex on edge

/**
 * Closest point on a line segment (a→b) to point p.
 * All inputs are { x, y } in any consistent coordinate space.
 * Returns { x, y, t } where t ∈ [0,1] is the interpolation parameter.
 */
const closestPointOnSegment = (p, a, b) => {
  const abx = b.x - a.x, aby = b.y - a.y
  const len2 = abx * abx + aby * aby
  if (len2 === 0) return { x: a.x, y: a.y, t: 0 }
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2))
  return { x: a.x + t * abx, y: a.y + t * aby, t }
}

/**
 * Polygon editing layer for Leaflet (CRS.Simple).
 * All Leaflet objects are imperative — zero React renders during drag.
 *
 * Vertex drag: mousedown on a vertex marker → map mousemove → mouseup commits.
 * Polygon drag: mousedown on polygon fill → map mousemove → mouseup commits.
 * Edge ghost: hovering near an edge shows a ghost vertex (lighter blue).
 *   Clicking the ghost inserts a new vertex at that position.
 * Map dragging is disabled during any drag and re-enabled on mouseup.
 *
 * Props:
 *   points        — array of {x, y} in pixel space (currentPoints from usePolygonEdit)
 *   updateVertex  — (index, {x, y}) => void
 *   translateAll  — (dx, dy) => void
 *   insertVertex  — (afterIndex, {x, y}) => void
 */
export const PolygonEditLayer = ({ points, updateVertex, translateAll, insertVertex }) => {
  const map = useMap()
  const { palette } = useTheme()

  // Drag state refs
  const draggingVertex = useRef(null)
  const draggingPolygon = useRef(false)
  const dragStartLatLng = useRef(null)

  // Ghost edge state refs
  const ghostMarker = useRef(null)
  const ghostEdge = useRef(null)   // { segmentIndex, point: {x, y} } or null

  // Imperative layer refs
  const fillRef = useRef(null)
  const outlineRef = useRef(null)
  const markersRef = useRef([])

  const fillStyle = useMemo(() => ({
    color: palette.warning.main,
    fillColor: palette.warning.light,
    fillOpacity: 0.15,
    weight: 2,
  }), [palette.warning.main, palette.warning.light])

  const vertexStyle = useMemo(() => ({
    radius: 7,
    color: palette.warning.dark,
    fillColor: palette.warning.light,
    fillOpacity: 0.9,
    weight: 2,
  }), [palette.warning.dark, palette.warning.light])

  const ghostStyle = useMemo(() => ({
    radius: 7,
    color: palette.info.main,
    fillColor: palette.info.light,
    fillOpacity: 0.75,
    weight: 2,
  }), [palette.info.main, palette.info.light])

  const getMarkerPositions = () => markersRef.current.map(m => m.getLatLng())

  // Always call the latest insertVertex without recreating the ghost marker's click handler
  const insertVertexRef = useRef(insertVertex)
  useEffect(() => { insertVertexRef.current = insertVertex }, [insertVertex])

  // Show or hide the ghost marker at a given latlng.
  // The click handler lives on the marker itself (not useMapEvents) so that
  // L.DomEvent.stopPropagation prevents the map-level click from firing,
  // which would otherwise deselect the active plot via MapZoomController.
  const showGhost = (latlng) => {
    if (!ghostMarker.current) {
      const m = L.circleMarker(latlng, ghostStyle).addTo(map)
      m.getElement().style.cursor = 'copy'
      m.on('click', (e) => {
        L.DomEvent.stopPropagation(e)
        if (ghostEdge.current) {
          insertVertexRef.current(ghostEdge.current.segmentIndex, ghostEdge.current.point)
          hideGhost()
        }
      })
      ghostMarker.current = m
    } else {
      ghostMarker.current.setLatLng(latlng)
      if (!map.hasLayer(ghostMarker.current)) ghostMarker.current.addTo(map)
    }
  }

  const hideGhost = () => {
    ghostMarker.current?.remove()
    ghostMarker.current = null
    ghostEdge.current = null
  }

  // Sync ghost marker style when palette changes
  useEffect(() => {
    ghostMarker.current?.setStyle(ghostStyle)
  }, [ghostStyle])

  // Disable map dragging for the lifetime of the edit layer
  useEffect(() => {
    map.dragging.disable()
    return () => map.dragging.enable()
  }, [map])

  // Create/recreate all vertex layers when point count changes
  useEffect(() => {
    if (!map || !points || points.length < 3) return

    markersRef.current.forEach(m => m.remove())
    fillRef.current?.remove()
    outlineRef.current?.remove()
    hideGhost()

    const latlngs = points.map(p => [p.y, p.x])

    const fill = L.polygon(latlngs, fillStyle).addTo(map)
    fill.on('mousedown', (e) => {
      if (draggingVertex.current !== null) return
      L.DomEvent.stopPropagation(e)
      draggingPolygon.current = true
      dragStartLatLng.current = e.latlng
      map.getContainer().style.cursor = 'grabbing'
    })
    fillRef.current = fill

    const markers = latlngs.map((ll, i) => {
      const m = L.circleMarker(ll, vertexStyle).addTo(map)
      const el = m.getElement()
      if (el) {
        el.style.cursor = 'crosshair'
        el.addEventListener('mousedown', (e) => {
          e.stopPropagation()
          draggingVertex.current = i
          draggingPolygon.current = false
          hideGhost()
          map.getContainer().style.cursor = 'crosshair'
        })
      }
      return m
    })
    markersRef.current = markers

    return () => {
      markers.forEach(m => m.remove())
      fill.remove()
      outlineRef.current?.remove()
      hideGhost()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, points.length])

  // Sync positions when points values change after a commit
  useEffect(() => {
    if (!points || points.length < 3) return
    markersRef.current.forEach((m, i) => {
      if (points[i]) m.setLatLng([points[i].y, points[i].x])
    })
    const latlngs = points.map(p => [p.y, p.x])
    fillRef.current?.setLatLngs(latlngs)
    outlineRef.current?.setLatLngs([...latlngs, latlngs[0]])
  }, [points])

  useMapEvents({
    mousemove: (e) => {
      if (draggingVertex.current !== null) {
        markersRef.current[draggingVertex.current]?.setLatLng(e.latlng)
        outlineRef.current?.setLatLngs([...getMarkerPositions(), markersRef.current[0]?.getLatLng()])
        fillRef.current?.setLatLngs(getMarkerPositions())
        return
      }

      if (draggingPolygon.current && dragStartLatLng.current) {
        const dLat = e.latlng.lat - dragStartLatLng.current.lat
        const dLng = e.latlng.lng - dragStartLatLng.current.lng
        markersRef.current.forEach((m, i) => {
          if (!points[i]) return
          m.setLatLng([points[i].y + dLat, points[i].x + dLng])
        })
        outlineRef.current?.setLatLngs([...getMarkerPositions(), markersRef.current[0]?.getLatLng()])
        fillRef.current?.setLatLngs(getMarkerPositions())
        return
      }

      // Ghost vertex: find closest edge to cursor in screen space
      const cursorPx = map.latLngToContainerPoint(e.latlng)

      // Suppress ghost when near an existing vertex so the user can drag it
      const nearVertex = points.some(p => {
        const vPx = map.latLngToContainerPoint([p.y, p.x])
        const dx = cursorPx.x - vPx.x, dy = cursorPx.y - vPx.y
        return Math.sqrt(dx * dx + dy * dy) < EDGE_SNAP_PX
      })
      if (nearVertex) { hideGhost(); return }

      let bestDist = EDGE_SNAP_PX
      let bestEdge = null

      for (let i = 0; i < points.length; i++) {
        const next = (i + 1) % points.length
        const aPx = map.latLngToContainerPoint([points[i].y, points[i].x])
        const bPx = map.latLngToContainerPoint([points[next].y, points[next].x])
        const snap = closestPointOnSegment(cursorPx, aPx, bPx)
        const dx = cursorPx.x - snap.x, dy = cursorPx.y - snap.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < bestDist) {
          bestDist = dist
          // Map the t parameter back to map coordinates
          const snapMap = {
            x: points[i].x + snap.t * (points[next].x - points[i].x),
            y: points[i].y + snap.t * (points[next].y - points[i].y),
          }
          bestEdge = { segmentIndex: i, point: snapMap }
        }
      }

      if (bestEdge) {
        showGhost([bestEdge.point.y, bestEdge.point.x])
        ghostEdge.current = bestEdge
      } else {
        hideGhost()
      }
    },

    mouseup: (e) => {
      if (draggingVertex.current !== null) {
        const i = draggingVertex.current
        const ll = markersRef.current[i]?.getLatLng()
        if (ll) updateVertex(i, { x: ll.lng, y: ll.lat })
        draggingVertex.current = null
      } else if (draggingPolygon.current && dragStartLatLng.current) {
        const dLat = e.latlng.lat - dragStartLatLng.current.lat
        const dLng = e.latlng.lng - dragStartLatLng.current.lng
        translateAll(dLng, dLat)
        draggingPolygon.current = false
        dragStartLatLng.current = null
      }
      map.getContainer().style.cursor = ''
    },

    mouseout: () => {
      hideGhost()
    },
  })

  return null
}
